import React, { useState, useEffect } from "react";
import {
  Building2,
  Award,
  Shield,
  Quote,
  Calendar,
  Users,
  Clock,
  Timer,
  CalendarCheck,
  TrendingUp,
  Activity,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";

// Import separated components
import HolidaySlider from "./HolidaySlider";
import NoticeBoardCard from "./NoticeBoardCard";
import MetricTile from "./MetricTile";
import CompanyValuesSlider from "./CompanyValuesSlider";

// Import constants
import { monthlyThemes, dailyQuotes } from "../constants/dashboardQuotes";

const DashboardHeader = ({
  announcements,
  metricsData,
  attendanceStatus,
  liveWorkedTime,
  shiftRemaining,
  hasManagementAuthority,
  holidays,
  leaveData,
  apiBaseUrl,
  onNoticeClick,
  onCircularClick,
  onLeaveClick,
}) => {
  const { user, hasRole } = useAuth();
  const [quote, setQuote] = useState(null);

  // Get daily quote based on current date
  useEffect(() => {
    const today = new Date();
    const month = today.getMonth(); // 0-11
    const day = today.getDate(); // 1-31

    const theme = monthlyThemes[month] || "Daily Motivation";
    const quoteText = dailyQuotes[month]?.[day];

    if (quoteText) {
      setQuote({ text: quoteText, author: theme });
    } else {
      setQuote({
        text: "Make today great. Your attitude and effort define your success.",
        author: "Daily Focus",
      });
    }
  }, []);

  if (!user) {
    return null;
  }

  const customColors = user.customColors || {};
  const announcement = announcements?.[0];
  const userLogo = user.profileImage
    ? `data:${user.profileImageType};base64,${user.profileImage}`
    : null;

  const formatTime24To12 = (time24) => {
    if (!time24) return "";
    const [hour, minute] = time24.split(":");
    const date = new Date();
    date.setHours(+hour, +minute);

    let formatted = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      hour12: true,
    });

    if (minute !== "00") {
      formatted = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    return formatted.toLowerCase().replace(":00", "").replace(/\s/g, " ");
  };

  const getAuthorityBadges = () => {
    const roles = user.authorities || [];
    const topRoles = roles.slice(0, 3);
    const extraCount = roles.length - topRoles.length;

    const formatRole = (role) => {
      const cleanRole = role.replace("ROLE_", "").toLowerCase();
      if (cleanRole === "hr") return "HR";
      if (cleanRole === "crm-admin") return "CRM Admin";
      if (cleanRole === "inventory-manager") return "Inventory";
      return cleanRole.charAt(0).toUpperCase() + cleanRole.slice(1);
    };

    return (
      <>
        {topRoles.map((auth, index) => {
          const roleKey = auth.replace("ROLE_", "");
          const isManager = roleKey === "MANAGER";
          const bgColor = isManager
            ? customColors.accent || "#f27150"
            : customColors.active || "#3b82f6";

          return (
            <span
              key={auth + index}
              className="px-2 py-0.5 rounded-md text-xs font-bold uppercase tracking-wide border shadow-sm"
              style={{
                backgroundColor: bgColor + "15",
                color: bgColor,
                borderColor: bgColor + "30",
                letterSpacing: "0.5px",
              }}
            >
              {isManager && <Award className="w-3 h-3 inline mr-1" />}
              {formatRole(roleKey)}
            </span>
          );
        })}
        {extraCount > 0 && (
          <span
            className="px-2 py-0.5 rounded-md text-xs font-bold border shadow-sm"
            style={{
              backgroundColor: (customColors.textMuted || "#94a3b8") + "15",
              color: customColors.textMuted || "#94a3b8",
              borderColor: (customColors.textMuted || "#94a3b8") + "30",
            }}
          >
            +{extraCount}
          </span>
        )}
      </>
    );
  };

  const getPermissionCount = () => user.authorities?.length || 0;

  const getWorkingCardColor = () => {
    if (!attendanceStatus?.attendanceStatus || !attendanceStatus?.punchInTime) {
      return customColors.textMuted || "#94a3b8";
    }

    if (!attendanceStatus.officeTiming?.shiftTo)
      return customColors.warning || "#f59e0b";

    const now = new Date();
    const [shiftHour, shiftMinute] =
      attendanceStatus.officeTiming.shiftTo.split(":");
    const shiftEndTime = new Date();
    shiftEndTime.setHours(parseInt(shiftHour), parseInt(shiftMinute), 0, 0);

    return now > shiftEndTime
      ? customColors.success || "#10b981"
      : customColors.warning || "#f59e0b";
  };

  return (
    <div className="dashboard-header-root">
      {/* Main Header Card */}
      <div className="dashboard-hero">
        <div className="dashboard-hero__inner">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Avatar Section */}
              <div className="dashboard-hero__avatar-wrap">
                <div
                  className="dashboard-hero__avatar"
                  style={{
                    borderColor: customColors.borderPrimary || "#e2e8f0",
                  }}
                >
                  {userLogo ? (
                    <img
                      src={userLogo}
                      alt="Company Logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2
                      className="w-8 h-8"
                      style={{ color: customColors.textMuted || "#94a3b8" }}
                    />
                  )}
                </div>
                {user.attendanceStatus && (
                  <div
                    className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white shadow-sm"
                    style={{
                      backgroundColor: customColors.success || "#10b981",
                    }}
                  ></div>
                )}
              </div>

              {/* User Info Section */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h1
                    className="dashboard-hero__name"
                    style={{ color: customColors.textPrimary || "#1e293b" }}
                  >
                    {user.employeeName}
                  </h1>
                  <span
                    className="px-2 py-1 rounded-lg font-bold text-xs uppercase tracking-wide shadow-sm border"
                    style={{
                      backgroundColor:
                        (customColors.active || "#3b82f6") + "15",
                      color: customColors.active || "#3b82f6",
                      borderColor: (customColors.active || "#3b82f6") + "30",
                    }}
                  >
                    {user.role}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-sm mb-2">
                  <div className="flex items-center gap-1.5">
                    <Building2
                      className="w-4 h-4"
                      style={{
                        color: customColors.textSecondary || "#64748b",
                      }}
                    />
                    <span
                      className="font-medium truncate"
                      style={{
                        color: customColors.textSecondary || "#64748b",
                      }}
                    >
                      {user.companyName}
                    </span>
                  </div>
                  <div className="text-gray-300">•</div>
                  <div className="flex items-center gap-1.5">
                    <Calendar
                      className="w-4 h-4"
                      style={{ color: customColors.textMuted || "#94a3b8" }}
                    />
                    <span
                      className="text-xs font-medium"
                      style={{ color: customColors.textMuted || "#94a3b8" }}
                    >
                      {new Date().toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                </div>

                <div className="flex items-center flex-wrap gap-2">
                  {getAuthorityBadges()}
                  <div
                    className="px-2 py-0.5 rounded-md text-xs font-bold border shadow-sm"
                    style={{
                      backgroundColor:
                        (customColors.success || "#10b981") + "15",
                      color: customColors.success || "#10b981",
                      borderColor: (customColors.success || "#10b981") + "30",
                    }}
                  >
                    <Shield className="w-3 h-3 inline mr-1" />
                    {getPermissionCount()} PERMS
                  </div>
                </div>
              </div>
            </div>

            {/* Quote Section */}
            <div className="dashboard-quote-wrap">
              <div className="dashboard-quote">
                <div className="absolute top-0 right-0 w-16 h-16 opacity-5">
                  <Quote className="w-12 h-12 transform rotate-12" />
                </div>
                <div className="relative z-10">
                  {quote ? (
                    <>
                      <blockquote className="text-sm font-bold leading-tight mb-2">
                        <span
                          style={{
                            background: `linear-gradient(135deg, ${
                              customColors.active || "#3b82f6"
                            }, ${customColors.accent || "#f27150"})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            color: customColors.active || "#3b82f6",
                          }}
                        >
                          "{quote.text}"
                        </span>
                      </blockquote>
                      <cite
                        className="text-xs font-bold uppercase tracking-wider not-italic opacity-60"
                        style={{
                          color: customColors.textSecondary || "#64748b",
                        }}
                      >
                        — {quote.author}
                      </cite>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-300/40 rounded w-full animate-pulse"></div>
                      <div className="h-3 bg-gray-300/40 rounded w-4/5 animate-pulse"></div>
                      <div className="h-2 bg-gray-300/40 rounded w-1/2 ml-auto animate-pulse"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fitness Tracker Style Metrics */}
      <div className="dashboard-metric-grid">
        {hasManagementAuthority && hasManagementAuthority() && (
          <MetricTile
            icon={<Users className="w-4 h-4" />}
            value={metricsData?.totalEmployees || 0}
            label="Team"
            color={customColors.active}
            customColors={customColors}
          />
        )}

        {["ROLE_ADMIN", "ROLE_HR", "ROLE_CEO", "ROLE_MANAGER"].some((role) =>
          hasRole(role),
        ) && (
          <MetricTile
            icon={<TrendingUp className="w-4 h-4" />}
            value={`${metricsData?.attendanceRate || 0}%`}
            label="Present"
            color={customColors.highlight}
            // trend="up"
            // trendValue="+2%"
            customColors={customColors}
          />
        )}

        <MetricTile
          icon={<Clock className="w-4 h-4" />}
          value={attendanceStatus?.punchInTime || "—"}
          label="Punch In"
          color={customColors.success}
          customColors={customColors}
        />

        <MetricTile
          icon={<Activity className="w-4 h-4" />}
          value={
            attendanceStatus?.punchInTime && attendanceStatus?.attendanceStatus
              ? `${liveWorkedTime?.hours || 0}h ${
                  liveWorkedTime?.minutes || 0
                }m`
              : "—"
          }
          label="Active"
          color={getWorkingCardColor()}
          customColors={customColors}
        />

        <MetricTile
          icon={<Timer className="w-4 h-4" />}
          value={
            shiftRemaining?.hours !== undefined
              ? `${shiftRemaining.hours}h ${shiftRemaining.minutes}m`
              : "—"
          }
          label="Shift Remaining"
          color={customColors.warning}
          customColors={customColors}
        />

        <MetricTile
          icon={<CalendarCheck className="w-4 h-4" />}
          value={
            attendanceStatus?.officeTiming
              ? `${formatTime24To12(
                  attendanceStatus.officeTiming.shiftFrom,
                )}-${formatTime24To12(attendanceStatus.officeTiming.shiftTo)}`
              : "—"
          }
          label="Shift"
          color={customColors.accent}
          customColors={customColors}
        />
      </div>

      {/* 3-Column Layout with Sliders */}
      {announcement && (
        <div className="dashboard-highlights">
          <div className="col-span-1">
            <CompanyValuesSlider
              announcement={announcement}
              customColors={customColors}
            />
          </div>
          <div className="col-span-1">
            <HolidaySlider holidays={holidays} />
          </div>
          <div className="col-span-1">
            <NoticeBoardCard
              // notices={announcements}
              circulars={[]}
              leaveData={leaveData}
              apiBaseUrl={apiBaseUrl}
              onNoticeClick={onNoticeClick}
              onCircularClick={onCircularClick}
              onLeaveClick={onLeaveClick}
              className="h-full"
            />{" "}
          </div>
        </div>
      )}

      {/* Mobile Quote Card */}
      <div className="lg:hidden xl:hidden">
        <div
          className="rounded-xl p-4 relative overflow-hidden border shadow-sm"
          style={{
            background: `linear-gradient(135deg, ${
              (customColors.active || "#3b82f6") + "08"
            } 0%, ${(customColors.accent || "#f27150") + "05"} 100%)`,
            borderColor: (customColors.active || "#3b82f6") + "20",
          }}
        >
          <div className="absolute top-0 right-0 w-12 h-12 opacity-10">
            <Quote className="w-full h-full transform rotate-12" />
          </div>
          <div className="relative z-10">
            {quote ? (
              <>
                <blockquote className="text-sm font-bold leading-tight mb-2">
                  <span
                    style={{
                      background: `linear-gradient(135deg, ${
                        customColors.active || "#3b82f6"
                      }, ${customColors.accent || "#f27150"})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      color: customColors.active || "#3b82f6",
                    }}
                  >
                    "{quote.text}"
                  </span>
                </blockquote>
                <cite
                  className="text-xs font-bold uppercase tracking-wider not-italic opacity-60"
                  style={{ color: customColors.textSecondary || "#64748b" }}
                >
                  — {quote.author}
                </cite>
              </>
            ) : (
              <div className="space-y-2">
                <div className="h-3 bg-gray-300/40 rounded w-full animate-pulse"></div>
                <div className="h-3 bg-gray-300/40 rounded w-4/5 animate-pulse"></div>
                <div className="h-2 bg-gray-300/40 rounded w-1/2 ml-auto animate-pulse"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;
